import { Controller } from '@hotwired/stimulus';
import { markRequiredEmpties, refreshNeedsValue, blockingGroups, clearAllMarks, anyMarks } from 'helpers/required-field-marks';

// Required-field enforcement for every CFS form, scribe or not (the AI Scribe runs the
// same sweep after its fill passes). Two layers:
//
// 1. When native validation blocks a save, the browser only surfaces the FIRST invalid
//    control (focus + a bubble) — the sweep marks every required-but-empty field with the
//    orange dotted cue so the whole picture shows at once.
// 2. Control types that carry no native `required` attribute (wysiwyg, checkboxes,
//    dropdowns, toggles) used to sail through to the server. The submit handler now blocks
//    the save when any visible required field is still empty, runs the same sweep, and
//    scrolls the first offender into view. Hidden fields never block — a user can't fix
//    what they can't see — and a submitter with formnovalidate is honored.
export default class extends Controller {
  static targets = ['clearMarksButton'];

  connect() {
    // 'invalid' does not bubble — capture it. The browser fires one event per failing
    // control in the same tick, so the sweep is batched behind a single rAF.
    this._boundInvalid = this._onInvalid.bind(this);
    this.element.addEventListener('invalid', this._boundInvalid, true);
    // The block listens on DOCUMENT in the capture phase, not on the form: at the target,
    // listeners run in registration order regardless of capture, and auto-save (registered
    // first) resubmits natively via element.submit() — which fires no submit event at all —
    // before a same-node blocker could act. Document capture runs before every at-target
    // listener, whoever registered first.
    this._boundSubmit = this._onSubmit.bind(this);
    document.addEventListener('submit', this._boundSubmit, true);
    // Typing into a flagged field clears its cue the moment it holds a value. An Impulse select
    // fires neither input nor change when an option is picked or cleared, only its own events.
    this._boundEdit = this._onEdit.bind(this);
    this.element.addEventListener('input', this._boundEdit);
    this.element.addEventListener('change', this._boundEdit);
    this.element.addEventListener('awc-autocomplete:commit', this._boundEdit);
    this.element.addEventListener('awc-autocomplete:clear', this._boundEdit);
    // Snapshot each save button's pristine markup. The legacy .custom-form-save click
    // handler swaps in a "Saving…" spinner and disables the button BEFORE any submit event
    // fires, and only its own validation-failure paths restore it — when WE refuse the
    // submit it never learns the save died, so we restore from this snapshot or the button
    // stays bricked on "Saving…" until a reload.
    this.saveButtonHTML = new Map();
    this.element.querySelectorAll('.custom-form-save').forEach((btn) => {
      this.saveButtonHTML.set(btn, btn.innerHTML);
    });
  }

  disconnect() {
    this.element.removeEventListener('invalid', this._boundInvalid, true);
    document.removeEventListener('submit', this._boundSubmit, true);
    this.element.removeEventListener('input', this._boundEdit);
    this.element.removeEventListener('change', this._boundEdit);
    this.element.removeEventListener('awc-autocomplete:commit', this._boundEdit);
    this.element.removeEventListener('awc-autocomplete:clear', this._boundEdit);
  }

  _onInvalid() {
    if (this.sweepQueued) return;
    this.sweepQueued = true;
    requestAnimationFrame(() => {
      this.sweepQueued = false;
      markRequiredEmpties(this.element);
      this._showClearMarks();
    });
  }

  _onSubmit(event) {
    if (event.target !== this.element) return;
    if (event.submitter?.formNoValidate) return;
    const offenders = blockingGroups(this.element);
    if (offenders.length === 0) return;
    event.preventDefault();
    // Halt the dispatch entirely: downstream at-target listeners must never see a submit
    // we've refused — auto-save in particular would resubmit natively and bypass the block.
    event.stopImmediatePropagation();
    this._restoreSaveButtons(event.submitter);
    markRequiredEmpties(this.element);
    this._showClearMarks();
    offenders[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  // Undo the legacy click handler's disable + spinner on every save button we snapshotted
  // (the legacy submit arrives via requestSubmit(), so event.submitter is null and the
  // mangled button must be found by class), plus the actual submitter if one exists.
  _restoreSaveButtons(submitter) {
    this.saveButtonHTML.forEach((html, btn) => {
      if (!btn.disabled) return;
      btn.disabled = false;
      btn.innerHTML = html;
    });
    if (submitter && submitter.disabled) submitter.disabled = false;
  }

  // The Clear highlights button next to the form title: dismiss every marking at once
  // without touching a value. Shared with the scribe — on scribe pages its own action also
  // runs and hands field ownership to the therapist.
  clearMarkings(event) {
    if (event) event.preventDefault();
    clearAllMarks(this.element);
    if (this.hasClearMarksButtonTarget) this.clearMarksButtonTarget.hidden = true;
  }

  // Two-way: reveal while any marking exists, retire the moment the last one clears.
  _showClearMarks() {
    if (!this.hasClearMarksButtonTarget) return;
    this.clearMarksButtonTarget.hidden = !anyMarks(this.element);
  }

  // Resolve the edited element to its datum control id — directly for named inputs, via
  // the host div for CKEditor edits, via the hidden value carrier for Impulse
  // autocompletes — and re-check just that field.
  _onEdit(event) {
    const t = event.target;
    if (!t || !(t instanceof Element)) return;
    let el = null;
    const host = t.closest?.('.wysiwyg-ckeditor');
    if (host?.dataset?.id) el = document.getElementById(host.dataset.id);
    if (!el && t.name && /\[datum\]\[/.test(t.name)) el = t;
    if (!el) {
      const autocomplete = t.closest?.('awc-autocomplete');
      el = autocomplete?.querySelector('[data-behavior="hidden-field"]') || null;
    }
    const id = el?.name?.match(/\[datum\]\[([^\]]+)\]/)?.[1];
    if (id) refreshNeedsValue(this.element, id);
    this._showClearMarks();
  }
}
